import React, { memo, Dispatch, SetStateAction } from 'react';
import ModalWithPattern from './ModalWithPattern';
import SvgQuestion from '../../../atoms/icons/SvgQuestion';
import styled, { CSSObject } from '@emotion/styled';
import { areEqual, isDarkMode, IsMobileViewPort } from '../../../utils/helpers';
import { getBackgroundTheme } from '../../../style';
import PrimaryButton from '../../../atoms/buttons/PrimaryButton';
import { mixpanelTrackWhatsAnOpeningOfferModalShown } from '../../../utils/mixpanel/infoModalShown';
import { defaultOfferAmount } from '../../../common';
import breakpoints from 'theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';
import { white } from 'theme/colors';
import FTWIcon from 'atoms/icons/FTWIcon';

const OpeningOfferContainer = styled('div')({
  display: 'initial',
});
const OpeningOfferDescContainer = styled('div')({
  marginBottom: 28,
  [breakpoints.down(639.05)]: {
    paddingLeft: 5,
    paddingRight: 5,
  },
});

const StyledSpan = styled('span')({
  fontSize: 13,
  lineHeight: '16px',
  fontWeight: 400,
  cursor: 'pointer',
  color: '#8392A7',
  '&:hover': {
    textDecoration: 'underline',
    color: '#2490DE',
  },
});

const StyledLinkSpan = styled('span')({
  fontSize: 14,
  lineHeight: '17px',
  fontWeight: 400,
  cursor: 'pointer',
  color: '#8392A7',
  marginTop: 5,
  '&:hover': {
    textDecoration: 'underline',
  },
});

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = {
  borderRadius: 10,
  maxHeight: 600,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  background: isDarkMode() ? '#212121' : white,
  border: 'unset',
};

const FirstDateModalBodyContainer = styled('div')({
  marginTop: 30,
});

const ButtonActionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: 0,
});

const TitleContainer = styled('h3')(() => ({
  fontStyle: 'normal',
  fontWeight: 400,
  color: isDarkMode() ? '#CFCFCF' : '#333333',
  fontSize: IsMobileViewPort ? 24 : 22,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
  marginTop: 8,
  marginBottom: 20,
  fontFamily: FONT_FAMILIES.secondary,
}));

const ContentList = styled('ul')({
  listStyleType: 'disc',
  lineHeight: '24px',
  minHeight: 100,
  paddingLeft: 20,
});

const ContentListItem = styled('li')(
  (): CSSObject => ({
    textAlign: 'left',
    paddingBottom: 10,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '-0.3px',
    fontFamily: FONT_FAMILIES.primary,
    color: isDarkMode() ? '#AFB0B1' : '#595959',
  })
);

interface OpeningOfferModalProps {
  customStyle?: React.CSSProperties;
  iconOnly?: boolean;
  linkStyle?: Record<string, unknown>;
  onClose?(): void;
  svgIconHeight?: number;
  svgIconWeight?: number;
  openEditOffer: any;
  currencySymbols: any;
  showOpeningOffer: boolean;
  setShowOpenningOffer: Dispatch<SetStateAction<boolean>>;
}

const OpeningOfferModal = ({
  iconOnly = false,
  linkStyle = {},
  svgIconWeight = 13,
  svgIconHeight = 13,
  customStyle = {},
  openEditOffer,
  currencySymbols,
  showOpeningOffer,
  setShowOpenningOffer,
}: OpeningOfferModalProps): JSX.Element => {
  const handleOnLinkClick = e => {
    e.preventDefault();

    if (!showOpeningOffer) {
      setShowOpenningOffer(true);
      mixpanelTrackWhatsAnOpeningOfferModalShown();
    }
  };

  const handleCloseModal = () => {
    setShowOpenningOffer(false);
  };

  return (
    <OpeningOfferContainer style={customStyle}>
      <StyledSpan style={linkStyle} onClick={e => handleOnLinkClick(e)}>
        <SvgQuestion
          viewBox="0 0 512 500"
          styles={{
            fill: isDarkMode() ? '#71B5E4' : '#2490DE',
            height: svgIconHeight,
            width: svgIconWeight,
          }}
        />{' '}
        {iconOnly ? '' : 'What’s this?'}
      </StyledSpan>
      {showOpeningOffer && (
        <ModalWithPattern
          dataTestID="first-date-offer-tutorial-modal"
          isModalOpen={showOpeningOffer}
          modalContentLabel="First Date Offer Tutorial"
          closeOnOverlayClick
          closeRequest={handleCloseModal}
          closeBtnClassName="first-date-offer-tutorial-close"
          closeBtnDataTestId="first-date-offer-tutorial-close"
          onCloseClick={handleCloseModal}
          customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
          customContentStyle={
            IsMobileViewPort
              ? mobileContentStyle
              : {
                  maxWidth: 345,
                  background: getBackgroundTheme().block2,
                  border: 'unset',
                }
          }
        >
          <FirstDateModalBodyContainer>
            <FTWIcon />
            <TitleContainer>{`What is an Opening Offer?`}</TitleContainer>
            <OpeningOfferDescContainer>
              <ContentList>
                <ContentListItem>The default offer amount shown on your profile.</ContentListItem>
                <ContentListItem>
                  {`Your opening offer amount can be anywhere from ${currencySymbols.currencyLabel}${defaultOfferAmount.minValue} to ${currencySymbols.currencyLabel}${defaultOfferAmount.maxValue}.`}
                </ContentListItem>
                <ContentListItem>
                  Any offers you get that are less than this amount can still be countered by you, giving you the final
                  say.
                </ContentListItem>
              </ContentList>
            </OpeningOfferDescContainer>
            <ButtonActionContainer>
              <PrimaryButton
                color="blue"
                size="small"
                data-test-id="close-first-date-offer-modal"
                onClick={handleCloseModal}
                label="Got it"
                customStyle={{ marginBottom: 10, width: 'inherit' }}
              />
              <StyledLinkSpan
                onClick={() => {
                  openEditOffer();
                }}
              >
                Edit My Opening Offer
              </StyledLinkSpan>
            </ButtonActionContainer>
          </FirstDateModalBodyContainer>
        </ModalWithPattern>
      )}
    </OpeningOfferContainer>
  );
};

export default memo(OpeningOfferModal, areEqual);
